import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Context
import { AuthProvider } from "./context/AuthContext";
import { ProjectProvider } from "./context/ProjectContext";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProjects from "./pages/admin/AdminProjects";
import AdminFeedConfig from "./pages/admin/AdminFeedConfig";
import AdminArticles from "./pages/admin/AdminArticles";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminWriterApps from "./pages/admin/AdminWriterApps";
import AdminSeo from "./pages/admin/AdminSeo";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminGlobalAnalytics from "./pages/admin/AdminGlobalAnalytics";
import AdminGlobalSettings from "./pages/admin/AdminGlobalSettings";
import AdminScheduler from "./pages/admin/AdminScheduler";

// News Site Pages
import NewsHome from "./pages/news/NewsHome";
import NewsCategory from "./pages/news/NewsCategory";
import NewsArticle from "./pages/news/NewsArticle";
import NewsSearch from "./pages/news/NewsSearch";
import NewsAuth from "./pages/news/NewsAuth";
import WriterDashboard from "./pages/news/WriterDashboard";
import BecomeWriter from "./pages/news/BecomeWriter";

// Protected Route Component
import ProtectedRoute from "./components/ProtectedRoute";

// Hosts where the root path must render the admin panel (not the public news site).
// Comma-separated list via REACT_APP_ADMIN_HOSTS; defaults cover Netlify + custom admin domain + local dev.
const ADMIN_HOSTS = (
  process.env.REACT_APP_ADMIN_HOSTS ||
  "mediamogul.menelausholding.com,mmogul.netlify.app,localhost,127.0.0.1"
)
  .split(",")
  .map((h) => h.trim().toLowerCase())
  .filter(Boolean);

const isAdminHost = () => {
  const host = (window.location.host || "").toLowerCase().split(":")[0];
  return ADMIN_HOSTS.includes(host);
};

const RootRoute = () => {
  // ?preview=<slug> lets admins preview a news site even from the admin host
  const hasPreviewParam = new URLSearchParams(window.location.search).has("preview");
  if (isAdminHost() && !hasPreviewParam) {
    return <Navigate to="/admin/login" replace />;
  }
  return <NewsHome />;
};

function App() {
  return (
    <AuthProvider>
      <ProjectProvider>
        <BrowserRouter>
          <Routes>
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute roles={["admin", "editor"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/projects" element={<ProtectedRoute roles={["admin"]}><AdminProjects /></ProtectedRoute>} />
            <Route path="/admin/feeds" element={<ProtectedRoute roles={["admin"]}><AdminFeedConfig /></ProtectedRoute>} />
            <Route path="/admin/articles" element={<ProtectedRoute roles={["admin", "editor"]}><AdminArticles /></ProtectedRoute>} />
            <Route path="/admin/reviews" element={<ProtectedRoute roles={["admin", "editor"]}><AdminReviews /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/applications" element={<ProtectedRoute roles={["admin", "editor"]}><AdminWriterApps /></ProtectedRoute>} />
            <Route path="/admin/seo" element={<ProtectedRoute roles={["admin", "editor"]}><AdminSeo /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute roles={["admin", "editor"]}><AdminAnalytics /></ProtectedRoute>} />
            <Route path="/admin/global-analytics" element={<ProtectedRoute roles={["admin", "editor"]}><AdminGlobalAnalytics /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute roles={["admin"]}><AdminGlobalSettings /></ProtectedRoute>} />
            <Route path="/admin/scheduler" element={<ProtectedRoute roles={["admin", "editor"]}><AdminScheduler /></ProtectedRoute>} />

            {/* News Site Routes */}
            <Route path="/" element={<RootRoute />} />
            <Route path="/category/:category" element={<NewsCategory />} />
            <Route path="/article/:slug" element={<NewsArticle />} />
            <Route path="/search" element={<NewsSearch />} />
            <Route path="/auth" element={<NewsAuth />} />
            <Route path="/writer" element={<ProtectedRoute roles={["writer", "editor", "admin"]}><WriterDashboard /></ProtectedRoute>} />
            <Route path="/become-writer" element={<BecomeWriter />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </ProjectProvider>
    </AuthProvider>
  );
}

export default App;
